import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import styled from 'styled-components';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { uploadImage } from '../../utils/cloudinary';



const AddRoomButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #1e3a8a;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 2rem;
  
  &:hover {
    background-color: #1c3175;
  }
`;

const RoomsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
`;

const RoomCard = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const RoomImage = styled.div`
  height: 200px;
  background-image: url(${props => props.src || 'https://via.placeholder.com/300x200?text=Room+Image'});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const ImagePreview = styled.div`
  margin-top: 1rem;
  width: 100%;
  max-width: 300px;
  position: relative;
  
  img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    border: 1px solid #d1d5db;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255,255,255,.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  margin-left: 10px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const RoomContent = styled.div`
  padding: 1.5rem;
`;

const RoomTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  color: #1f2937;
`;

const RoomPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e3a8a;
  margin-bottom: 1rem;
`;

const RoomDescription = styled.p`
  color: #6b7280;
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

const RoomFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const FeatureTag = styled.span`
  background-color: #f3f4f6;
  color: #4b5563;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
`;

const RoomActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  
  &.edit {
    background-color: #dbeafe;
    color: #1e40af;
    
    &:hover {
      background-color: #bfdbfe;
    }
  }
  
  &.delete {
    background-color: #fee2e2;
    color: #b91c1c;
    
    &:hover {
      background-color: #fecaca;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #1f2937;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #1e3a8a;
    box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #1e3a8a;
    box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.1);
  }
`;

const FeatureInput = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
`;

const RemoveFeatureButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #f3f4f6;
  color: #4b5563;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  
  &:hover {
    background-color: #e5e7eb;
  }
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #1e3a8a;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  
  &:hover {
    background-color: #1c3175;
  }
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

const ConfirmationModal = styled(Modal)``;

const ConfirmationContent = styled(ModalContent)`
  max-width: 400px;
`;

const ConfirmationTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #1f2937;
`;

const ConfirmationMessage = styled.p`
  color: #6b7280;
  margin-bottom: 2rem;
`;

const ConfirmationActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const RoomsManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    capacity: 2,
    description: '',
    features: [],
    imageUrl: ''
  });
  const [newFeature, setNewFeature] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const roomsCollection = collection(db, 'rooms');
      const snapshot = await getDocs(roomsCollection);
      
      const roomsData = [];
      snapshot.forEach((doc) => {
        roomsData.push({ id: doc.id, ...doc.data() });
      });
      
      setRooms(roomsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setLoading(false);
    }
  };

  const handleAddRoom = () => {
    setFormData({
      name: '',
      price: '',
      capacity: 2,
      description: '',
      features: [],
      imageUrl: ''
    });
    setNewFeature('');
    setImageFile(null);
    setImagePreview('');
    setShowModal(true);
  };

  const handleEditRoom = (room) => {
    setCurrentRoom(room);
    setFormData({
      name: room.name || '',
      price: room.price || '',
      capacity: room.capacity || '',
      imageUrl: room.imageUrl || '',
      description: room.description || '',
      features: room.features || []
    });
    setShowModal(true);
  };

  const handleDeleteRoom = (room) => {
    setCurrentRoom(room);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteRoom = async () => {
    if (!currentRoom) return;
    
    try {
      await deleteDoc(doc(db, 'rooms', currentRoom.id));
      
      // Update local state
      setRooms(prevRooms => prevRooms.filter(room => room.id !== currentRoom.id));
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error('Error deleting room:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddFeature = () => {
    if (newFeature.trim() === '') return;
    
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, newFeature.trim()]
    }));
    
    setNewFeature('');
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setUploadMessage('');
    }
  };
  
  const handleImageUpload = async () => {
    if (!imageFile) return null;
    
    try {
      setIsUploading(true);
      const uploadResult = await uploadImage(imageFile);
      setIsUploading(false);
      setUploadMessage('Image uploaded successfully!');
      return uploadResult.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      setIsUploading(false);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Upload image if provided
      let imageUrl = formData.imageUrl;
      if (imageFile) {
        const uploadedUrl = await handleImageUpload();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        }
      }
      
      if (currentRoom) {
        // Update existing room
        await updateDoc(doc(db, 'rooms', currentRoom.id), {
          name: formData.name,
          price: parseFloat(formData.price),
          capacity: parseInt(formData.capacity),
          imageUrl: imageUrl,
          description: formData.description,
          features: formData.features,
          updatedAt: new Date()
        });
      } else {
        // Add new room
        await addDoc(collection(db, 'rooms'), {
          name: formData.name,
          price: parseFloat(formData.price),
          capacity: parseInt(formData.capacity),
          imageUrl: imageUrl,
          description: formData.description,
          features: formData.features,
          createdAt: new Date()
        });
      }
      
      // Refresh rooms list
      fetchRooms();
      
      // Close modal
      setShowModal(false);
      
      // Reset form and image states
      setFormData({
        name: '',
        price: '',
        capacity: '',
        imageUrl: '',
        description: '',
        features: []
      });
      setImageFile(null);
      setImagePreview('');
      setUploadMessage('');
    } catch (error) {
      console.error('Error saving room:', error);
    }
  };

  return (
    <>
      <AddRoomButton onClick={handleAddRoom}>
        <FaPlus /> Add New Room
      </AddRoomButton>
      
      {loading ? (
        <div>Loading rooms...</div>
      ) : (
        <RoomsGrid>
          {rooms.map(room => (
            <RoomCard key={room.id}>
              <RoomImage src={room.imageUrl} />
              <RoomContent>
                <RoomTitle>{room.name}</RoomTitle>
                <RoomPrice>${room.price} / night</RoomPrice>
                <RoomDescription>{room.description}</RoomDescription>
                <RoomFeatures>
                  {room.features && room.features.map((feature, index) => (
                      <FeatureTag key={index}>{feature}</FeatureTag>
                    ))}
                </RoomFeatures>
                <RoomActions>
                  <ActionButton className="edit" onClick={() => handleEditRoom(room)}>
                    <FaEdit />
                  </ActionButton>
                  <ActionButton className="delete" onClick={() => handleDeleteRoom(room)}>
                    <FaTrash />
                  </ActionButton>
                </RoomActions>
              </RoomContent>
            </RoomCard>
          ))}
        </RoomsGrid>
      )}
      
      {/* Add/Edit Room Modal */}
      {showModal && (
        <Modal>
          <ModalContent>
            <ModalTitle>{currentRoom ? 'Edit Room' : 'Add New Room'}</ModalTitle>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Room Name</Label>
                <Input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="price">Price per Night ($)</Label>
                <Input 
                  type="number" 
                  id="price" 
                  name="price" 
                  value={formData.price} 
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="capacity">Capacity (persons)</Label>
                <Input 
                  type="number" 
                  id="capacity" 
                  name="capacity" 
                  value={formData.capacity} 
                  onChange={handleInputChange}
                  min="1"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="roomImage">Room Image</Label>
                <Input 
                  type="file" 
                  id="roomImage" 
                  accept="image/*" 
                  onChange={handleImageChange}
                />
                {imagePreview && (
                  <ImagePreview>
                    <img src={imagePreview} alt="Preview" />
                  </ImagePreview>
                )}
                {isUploading && (
                  <div style={{ marginTop: '10px', color: '#1e3a8a' }}>
                    Uploading... <LoadingSpinner />
                  </div>
                )}
                {uploadMessage && (
                  <div style={{ marginTop: '10px', color: '#10b981' }}>
                    {uploadMessage}
                  </div>
                )}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="imageUrl">Image URL (or upload above)</Label>
                <Input 
                  type="text" 
                  id="imageUrl" 
                  name="imageUrl" 
                  value={formData.imageUrl} 
                  onChange={handleInputChange}
                  placeholder="https://example.com/room-image.jpg"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  name="description" 
                  value={formData.description} 
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Features</Label>
                <FeatureInput>
                  <Input 
                    type="text" 
                    value={newFeature} 
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="Add a feature (e.g. Free Wi-Fi)"
                  />
                  <SaveButton type="button" onClick={handleAddFeature}>Add</SaveButton>
                </FeatureInput>
                <FeaturesList>
                  {formData.features.map((feature, index) => (
                    <FeatureItem key={index}>
                      {feature}
                      <RemoveFeatureButton 
                        type="button" 
                        onClick={() => handleRemoveFeature(index)}
                      >
                        ×
                      </RemoveFeatureButton>
                    </FeatureItem>
                  ))}
                </FeaturesList>
              </FormGroup>
              
              <ModalActions>
                <CancelButton type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </CancelButton>
                <SaveButton type="submit">
                  {currentRoom ? 'Update Room' : 'Add Room'}
                </SaveButton>
              </ModalActions>
            </Form>
          </ModalContent>
        </Modal>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <ConfirmationModal>
          <ConfirmationContent>
            <ConfirmationTitle>Delete Room</ConfirmationTitle>
            <ConfirmationMessage>
              Are you sure you want to delete "{currentRoom?.name}"? This action cannot be undone.
            </ConfirmationMessage>
            <ConfirmationActions>
              <CancelButton onClick={() => setShowDeleteConfirmation(false)}>
                Cancel
              </CancelButton>
              <SaveButton onClick={confirmDeleteRoom} style={{ backgroundColor: '#dc2626' }}>
                Delete
              </SaveButton>
            </ConfirmationActions>
          </ConfirmationContent>
        </ConfirmationModal>
      )}
    </>
  );
};

export default RoomsManagement;
